import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Eye, EyeOff, Video, FileText, Award, Calendar,
  TrendingUp, CheckCircle, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TalentOverview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/talent/me`);
      setProfile(response.data);
    } catch (error) {
      // Profile doesn't exist yet
      setProfile({
        discoverable: false,
        subscription_status: 'inactive',
        profile_completeness_score: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleDiscoverable = async () => {
    if (!profile.subscription_status || profile.subscription_status !== 'active') {
      toast.error('Active subscription required to be discoverable');
      navigate('/talent/me/subscribe');
      return;
    }

    setToggling(true);
    try {
      await axios.patch(`${API}/talent/me/discoverable`, {
        discoverable: !profile.discoverable
      });
      
      setProfile({ ...profile, discoverable: !profile.discoverable });
      toast.success(profile.discoverable ? 'Profile hidden from employers' : 'Profile is now discoverable!');
    } catch (error) {
      toast.error('Failed to update visibility');
    } finally {
      setToggling(false);
    }
  };

  const hasActiveSubscription = profile?.subscription_status === 'active' && 
    profile?.subscription_ends_at && 
    new Date(profile.subscription_ends_at) > new Date();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">GoTalent Dashboard</h1>
          <p className="text-muted-foreground">Manage your discoverable profile and subscriptions</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Subscription Status */}
            <Card>
              <CardHeader>
                <CardTitle>Subscription Status</CardTitle>
              </CardHeader>
              <CardContent>
                {hasActiveSubscription ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-foreground">Active Subscription</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Expires: {new Date(profile.subscription_ends_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <span className="font-semibold text-foreground">No Active Subscription</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Subscribe to make your profile discoverable to employers
                    </p>
                    <button
                      onClick={() => navigate('/talent/subscribe')}
                      className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Subscribe Now - From $3
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Discoverability Toggle */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Visibility</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-foreground mb-1">
                      Make my profile discoverable
                    </div>
                    <p className="text-sm text-muted-foreground">
                      When ON, employers can find and contact you
                    </p>
                  </div>
                  <button
                    onClick={toggleDiscoverable}
                    disabled={toggling || !hasActiveSubscription}
                    className={`relative w-14 h-7 rounded-full transition-all ${
                      profile.discoverable 
                        ? 'bg-green-600' 
                        : 'bg-muted'
                    } ${!hasActiveSubscription ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className={`absolute top-0.5 ${
                      profile.discoverable ? 'right-0.5' : 'left-0.5'
                    } w-6 h-6 bg-white rounded-full transition-all shadow-md flex items-center justify-center`}>
                      {profile.discoverable ? (
                        <Eye className="w-3 h-3 text-green-600" />
                      ) : (
                        <EyeOff className="w-3 h-3 text-muted-foreground" />
                      )}
                    </div>
                  </button>
                </div>
                {!hasActiveSubscription && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                    Active subscription required to toggle visibility
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Profile Completeness */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Completeness</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Completeness Score</span>
                    <span className="font-semibold text-foreground">{profile.profile_completeness_score || 0}%</span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-teal-500 transition-all"
                      style={{ width: `${profile.profile_completeness_score || 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Basic info</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {profile.video_cover_letter_ref_id ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-muted"></div>
                    )}
                    <span>Video introduction</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {profile.resume_ref_id ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-muted"></div>
                    )}
                    <span>Resume uploaded</span>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/talent/me/profile')}
                  className="w-full mt-4 px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors font-medium"
                >
                  Complete Your Profile
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Side Column */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <button
                  onClick={() => navigate('/talent/me/profile')}
                  className="w-full flex items-center gap-3 px-4 py-3 border border-border rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <FileText className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Edit Profile</span>
                </button>
                <button
                  onClick={() => navigate('/talent/me/media')}
                  className="w-full flex items-center gap-3 px-4 py-3 border border-border rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <Video className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Manage Media</span>
                </button>
                <button
                  onClick={() => navigate('/talent/me/offers')}
                  className="w-full flex items-center gap-3 px-4 py-3 border border-border rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <Award className="w-5 h-5 text-green-600" />
                  <span className="font-medium">View Offers</span>
                </button>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Profile Views</span>
                  <span className="font-semibold text-foreground">0</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Offers Received</span>
                  <span className="font-semibold text-foreground">0</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Interviews</span>
                  <span className="font-semibold text-foreground">0</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentOverview;
